// Mini App theme bridge. Has two responsibilities:
//
//   1. Decide the active color scheme: prefer the Telegram themeParams.isDark
//      signal when running inside Telegram; fall back to the user's OS
//      `prefers-color-scheme` outside Telegram (dev preview, jsdom).
//
//   2. Apply that decision by toggling the `dark` class on a root element
//      (defaults to <html>) so Tailwind's `.dark` selector activates the
//      dark palette declared in src/app/index.css.
//
// The pure resolver / applier are split from the subscription wiring so the
// logic is unit-testable without touching the SDK or matchMedia. The
// subscription helper is wired once at app boot from app/providers.

export type ColorScheme = "dark" | "light";

export interface ResolveColorSchemeInput {
  /**
   * `true`/`false` if Telegram has reported a definite theme, `null`/`undefined`
   * when running outside Telegram or before themeParams have mounted.
   */
  tgIsDark?: boolean | null;
  /** Result of `window.matchMedia("(prefers-color-scheme: dark)").matches`. */
  prefersDark: boolean;
}

/**
 * Telegram's signal wins when present — Mini Apps inherit the user's
 * Telegram theme rather than the OS theme so the chrome (header, in-app
 * popups) and the WebView agree. The OS preference is only the fallback.
 */
export function resolveColorScheme(input: ResolveColorSchemeInput): ColorScheme {
  if (input.tgIsDark === true) return "dark";
  if (input.tgIsDark === false) return "light";
  return input.prefersDark ? "dark" : "light";
}

/**
 * Toggle the Tailwind dark-mode class on the supplied element. Idempotent —
 * calling twice with the same scheme does not produce DOM mutations.
 */
export function applyColorScheme(
  root: HTMLElement,
  scheme: ColorScheme,
): void {
  if (scheme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}
